import { useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useRouter } from '@tanstack/react-router'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { SkeletonRows } from '@/components/ui/Skeleton'
import { EmptyState } from '@/components/ui/EmptyState'
import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Select'
import { Sheet } from '@/components/ui/Sheet'
import { StatusChip } from '@/components/ui/StatusChip'
import { OwnerDeleteButton } from '@/features/owner/OwnerDelete'
import { useProfile } from '@/auth/SessionProvider'
import { canWriteDepartment } from '@/auth/profile'
import { listBudgets, listOpenQuarters, startBudget } from '@/data/budgets'
import { formatKwacha } from '@/lib/money'
import { currentQuarterId } from '@/lib/periods'
import { quarterLabel } from './schemas'

const errText = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong. Please try again.')

export function BudgetsPage({ deptId }: { deptId: string }) {
  const profile = useProfile()
  const canWrite = canWriteDepartment(profile, deptId)
  const router = useRouter()
  const qc = useQueryClient()
  const thisQuarter = currentQuarterId()

  const budgetsQ = useQuery({ queryKey: ['budgets', deptId], queryFn: () => listBudgets(deptId) })
  const quartersQ = useQuery({ queryKey: ['open-quarters', thisQuarter], queryFn: () => listOpenQuarters(thisQuarter) })

  const [sheet, setSheet] = useState(false)
  const [quarter, setQuarter] = useState('')
  const [formError, setFormError] = useState('')
  const busy = useRef(false)
  const startM = useMutation({ mutationFn: (q: string) => startBudget(deptId, q) })

  const budgets = budgetsQ.data ?? []
  const taken = new Set(budgets.map((b) => b.quarterId))
  const choices = (quartersQ.data ?? []).filter((q) => !taken.has(q.id))

  const openSheet = () => {
    setQuarter(choices[0]?.id ?? '')
    setFormError('')
    setSheet(true)
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (busy.current) return
    if (!quarter) {
      setFormError('Pick a quarter')
      return
    }
    busy.current = true
    setFormError('')
    try {
      await startM.mutateAsync(quarter)
      await qc.invalidateQueries({ queryKey: ['budgets', deptId] })
      setSheet(false)
      await router.navigate({ to: '/d/$deptId/budgets/$quarterId', params: { deptId, quarterId: quarter } })
    } catch (err) {
      setFormError(errText(err))
    } finally {
      busy.current = false
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-title font-semibold text-ink">Budgets</h2>
        <p className="mt-1 text-muted">A budget says what the department plans to spend in a quarter (three months).</p>
      </div>

      <Card>
        <CardHeader className="flex items-center justify-between gap-3">
          <h3 className="font-semibold text-ink">Your budgets</h3>
          {canWrite && (
            <Button size="lg" onClick={openSheet} disabled={quartersQ.isPending}>
              Start a budget
            </Button>
          )}
        </CardHeader>
        <CardBody>
          {budgetsQ.isPending ? (
            <SkeletonRows rows={4} />
          ) : budgetsQ.isError ? (
            <div>
              <p role="alert" className="text-body text-bad">{errText(budgetsQ.error)}</p>
              <Button variant="secondary" size="lg" className="mt-3" onClick={() => void budgetsQ.refetch()}>Try again</Button>
            </div>
          ) : budgets.length === 0 ? (
            <EmptyState
              title="No budgets yet"
              description={canWrite ? 'Start one for this quarter or the next.' : 'Nothing has been planned yet.'}
            />
          ) : (
            <ul className="divide-y divide-line">
              {budgets.map((b) => (
                <li key={b.id}>
                  <Link
                    to="/d/$deptId/budgets/$quarterId"
                    params={{ deptId, quarterId: b.quarterId }}
                    className="flex min-h-[56px] items-center justify-between gap-3 py-3 hover:bg-page"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-ink">{quarterLabel(b.quarterId)}</p>
                      <p className="text-body text-muted">
                        {b.lineCount === 0 ? 'No lines yet' : `${b.lineCount} ${b.lineCount === 1 ? 'line' : 'lines'}, ${formatKwacha(b.totalNgwee)}`}
                      </p>
                    </div>
                    <StatusChip status={b.status} />
                  </Link>
                  <OwnerDeleteButton table="budget" id={b.id} what="budget" wrap="flex flex-wrap gap-2 pb-3" />
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <Sheet isOpen={sheet} onClose={() => setSheet(false)} title="Start a budget">
        <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-4" noValidate>
          {choices.length === 0 ? (
            <p className="text-neutral">There is a budget for every open quarter already.</p>
          ) : (
            <Select
              label="Which quarter?"
              value={quarter}
              onChange={(e) => setQuarter(e.target.value)}
              options={choices.map((q) => ({ value: q.id, label: quarterLabel(q.id) }))}
            />
          )}
          {formError && <p role="alert" className="text-body text-bad">{formError}</p>}
          <Button type="submit" size="lg" isLoading={startM.isPending} disabled={choices.length === 0}>
            {startM.isPending ? 'Starting...' : 'Start budget'}
          </Button>
        </form>
      </Sheet>
    </div>
  )
}
